/**
 * Single source of truth for the workflow actions carried by `WorkflowStatus.actions`.
 *
 * The field is a list and, once IDS-7322 introduces multiple workflow permissions, the same user
 * may legitimately hold more than one action on the same document part. Every helper exposed here
 * therefore reasons on the whole list and never depends on the position of an element, so that the
 * outcome is stable regardless of the order in which the backend returns the actions.
 *
 * Before this module the action vocabulary was duplicated in four places, each with its own
 * matching rule and its own ordering, which silently disagreed as soon as a list held more than two
 * entries: `workflowStatusCategory.ts`, `InlineActionsPanel.tsx`, `DocumentPartTreeUtils.ts#getStyle`
 * and the two inline sorts in `DocumentPartTree.tsx`.
 */

export const SUBMIT_ACTION = "SUBMIT";
export const REJECT_ACTION = "REJECT";

/** Actions (any approval level) that mean the current user can approve the pending node. */
export const APPROVE_ACTIONS = ["APPROVE", "APPROVE2", "APPROVE3", "APPROVE4", "APPROVE5"];

const APPROVE_ACTION_SET = new Set(APPROVE_ACTIONS);

/**
 * Tells whether the action is an approval one, at any level.
 *
 * The match is exact, mirroring the case-sensitive checks this helper replaces: the backend always
 * emits upper-case actions, so accepting other casings here would make the add-in offer actions
 * that it does not offer today.
 */
export const isApproveAction = (action?: string | null): boolean =>
  typeof action === "string" && APPROVE_ACTION_SET.has(action);

/** Tells whether the list carries at least one approval action, at any level. */
export const hasApproveAction = (actions?: readonly string[] | null): boolean =>
  Array.isArray(actions) && actions.some(isApproveAction);

/** Tells whether the list carries the submit action. */
export const hasSubmitAction = (actions?: readonly string[] | null): boolean =>
  Array.isArray(actions) && actions.includes(SUBMIT_ACTION);

const REJECT_WEIGHT = 0;
const OTHER_WEIGHT = 1;
const APPROVE_WEIGHT = 2;

/**
 * Ordering weight of an action: rejections first, approvals last, anything else (SUBMIT) in
 * between. The substring matching is intentional, it preserves the behavior of the sort that used
 * to live in `InlineActionsPanel` and that already won over the one in `DocumentPartTree`.
 */
const actionWeight = (action: string): number => {
  const normalizedAction = action.toLowerCase();
  if (normalizedAction.includes("reject")) {
    return REJECT_WEIGHT;
  }
  return normalizedAction.includes("approve") ? APPROVE_WEIGHT : OTHER_WEIGHT;
};

/** Approval level of a single action: 1 for `APPROVE` up to 5 for `APPROVE5`, 0 when not an approval. */
const approvalLevel = (action: string): number => APPROVE_ACTIONS.indexOf(action) + 1;

/**
 * Returns the actions to render, de-duplicated and in a deterministic order.
 *
 * Approvals are tie-broken by ascending level, so the first approval the user is expected to act on
 * comes first. De-duplication protects the rendering from repeated entries, which would otherwise
 * produce duplicate React keys.
 */
export const sortWorkflowActions = (actions?: readonly string[] | null): string[] => {
  const uniqueActions = Array.from(new Set(Array.isArray(actions) ? actions : [])).filter(
    (action): action is string => typeof action === "string" && action.length > 0
  );
  return uniqueActions.sort((a, b) => actionWeight(a) - actionWeight(b) || approvalLevel(a) - approvalLevel(b));
};
